import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { of } from 'rxjs';
import { SettingService } from '../services/setting.service';
import { environment } from '../../environments/environment';

// Shared state for the Inspectmite shell (sidebar + top bar).
//
// Layout state lives as classes on <body> (im-sidebar-collapsed, im-mobile-menu-open, dark-mode,
// colorblind-mode) so shell.css can drive everything from one place, the same way twin-ci's copy
// of the shell does.
//
// USER PREFERENCES (Quick Links, collapse, dark / colorblind mode) are stored per user in the
// database (api/user-preferences, table user_preferences) and shared with twin-ci and across
// devices. localStorage only caches them for this browser:
//   1. on load, the cache is applied at once (src/index.html does the body classes before
//      Angular starts, so there is no flash);
//   2. the server copy is then fetched and wins, and the cache is updated;
//   3. it is fetched again whenever the tab becomes visible, to pick up changes made in twin-ci
//      or on another device;
//   4. a change updates the page and cache immediately and is saved as a key merge.
export type DisplayMode = 'dark-mode' | 'colorblind-mode';

export interface Preferences {
    [key: string]: any;
}

// What a page asks the top bar to show on its left. Route data { title, subtitle } still works;
// this is for pages that need more, like the dashboard's greeting + date + weather.
export interface PageHeader {
    greeting?: boolean;
    // Replaces the route's title/subtitle, e.g. the inspection pages: "Inspection: Scheduler" +
    // the job's address and report number (subtitleLink makes the subtitle a link).
    title?: string;
    subtitle?: string;
    subtitleLink?: string;
    struck?: boolean;          // subtitle shown struck through (cancelled job)
    // Inspection pages: the title (the property address) opens this list of the order's reports
    // (mockup property-reports), newest first.
    reports?: PageHeaderReport[];
    // Inspection pages: the Home / Termite Inspection selector beside the address (mockup
    // inspection-service). serviceChange is called with the new value when it may be changed.
    service?: InspectionServiceKind;
    serviceChange?: (service: InspectionServiceKind) => void;
}

export type InspectionServiceKind = 'termite' | 'home';

export interface PageHeaderReport {
    number: string;            // "1234 RE"
    type: string;              // job_sub_type: Original, Reinspection, ...
    date: Date | null;         // inspection date from the Scheduling tab, when set
    link: string;              // that report's Overview
    current: boolean;
    cancelled: boolean;
}

const CACHE_KEY = 'im-preferences';

// Preference key => class on <body>. src/index.html repeats this map for the pre-Angular paint.
const BODY_CLASSES: { [key: string]: string } = {
    sidebar_collapsed: 'im-sidebar-collapsed',
    dark_mode: 'dark-mode',
    colorblind_mode: 'colorblind-mode'
};

@Injectable({
    providedIn: 'root'
})
export class ShellService {
    // One settings request shared by the sidebar and the top bar.
    readonly settings$: Observable<any>;
    // The tenant's sidebar logo URL ('' when none is set): the sidebar and the tour introduction.
    readonly sidebarLogo$: Observable<string>;

    /** URL of a logo file uploaded in Company Profile & Branding ('' for none). */
    /** A job's Home / Termite Inspection: termite unless home inspections are on and it is home. */
    static serviceOf(job, homeInspections: boolean): InspectionServiceKind {
        return homeInspections && job && job.job_service === 'home' ? 'home' : 'termite';
    }

    static mediaLogo(file: string): string {
        return file ? environment.doSpace + '/media/logo/0/' + file : '';
    }
    // The signed-in staff user's permission labels (twin-ci verify_me), for the menus. One request,
    // shared; empty for administrators and inspectors, and on error.
    readonly permissions$: Observable<string[]>;
    // Whether this tenant has Authorize.Net set up (get_setting_data card_payments); Manage Cards
    // is hidden without it. False until known and on error.
    readonly cardPayments$: Observable<boolean>;
    // Home / Termite inspections (get_setting_data home_inspections, HOME_INSPECTIONS in the
    // tenant's env). Off, every job is a termite inspection and nothing Home is shown. False on error.
    readonly homeInspections$: Observable<boolean>;

    private prefs: Preferences = {};
    private readonly prefsSubject = new BehaviorSubject<Preferences>({});
    readonly preferences$ = this.prefsSubject.asObservable();
    private pendingSaves = 0;
    private loaded = false;
    // True once the server copy has arrived (not just this browser's cache), for one-time prompts.
    preferencesReady = false;

    // The signed-in user's profile picture (api/account; Settings > My Account), for the top bar.
    private readonly photoSubject = new BehaviorSubject<string>(null);
    readonly accountPhoto$ = this.photoSubject.asObservable();
    private photoRequested = false;

    private readonly headerSubject = new BehaviorSubject<PageHeader>(null);
    readonly pageHeader$ = this.headerSubject.asObservable();

    constructor(settingService: SettingService, private http: HttpClient, private zone: NgZone) {
        this.settings$ = settingService.getSettingData().pipe(shareReplay(1));
        this.sidebarLogo$ = this.settings$.pipe(
            map(settings => ShellService.mediaLogo(settings && settings.settings_logo_sidebar_image)),
            catchError(() => of('')),
            shareReplay(1)
        );
        this.cardPayments$ = this.settings$.pipe(
            map(settings => !!(settings && settings.card_payments)),
            catchError(() => of(false)),
            shareReplay(1)
        );
        this.homeInspections$ = this.settings$.pipe(
            map(settings => !!(settings && settings.home_inspections)),
            catchError(() => of(false)),
            shareReplay(1)
        );
        this.permissions$ = this.http.get<{ permissions: string[] }>(environment.api + 'user-access').pipe(
            map(res => (res && res.permissions) || []),
            catchError(() => of([])),
            shareReplay(1)
        );
        // Mockup (2026-10-21): the sidebar collapses on its own in a window half the screen's
        // usable width or narrower (above the 780px phone layout, which has the hamburger menu).
        this.narrow = ShellService.isNarrowWindow();
        if (this.narrow) {
            this.applyCollapsed();
        }
        window.addEventListener('resize', () => {
            const narrow = ShellService.isNarrowWindow();
            if (narrow !== this.narrow) {
                this.zone.run(() => {
                    this.narrow = narrow;
                    this.narrowOverride = null;   // back to the automatic / saved state
                    this.applyCollapsed();
                });
            }
        });
    }

    // Auto-collapse state: in a narrow window the rail shows unless the user expands it there
    // (narrowOverride, not saved); the saved preference applies again in a wider window.
    private narrow = false;
    private narrowOverride: boolean = null;

    private static isNarrowWindow(): boolean {
        const width = window.innerWidth;
        const screenWidth = (window.screen && window.screen.availWidth) || width;
        return width > 780 && width <= screenWidth / 2;
    }

    private applyCollapsed() {
        document.body.classList.toggle('im-sidebar-collapsed', this.collapsed);
    }

    get user(): any {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch (e) {
            return {};
        }
    }

    // A page sets this in ngOnInit and clears it (null) in ngOnDestroy.
    setPageHeader(header: PageHeader) {
        this.headerSubject.next(header);
    }

    // ---- Preferences ----

    // Called once by the sidebar. Safe to call again; it only sets things up the first time.
    loadPreferences() {
        if (this.loaded) {
            return;
        }
        this.loaded = true;
        this.adopt(this.readCache(), false);
        this.fetchPreferences();
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.zone.run(() => this.fetchPreferences());
            }
        });
    }

    get(key: string, fallback: any = null): any {
        return this.prefs.hasOwnProperty(key) ? this.prefs[key] : fallback;
    }

    set(key: string, value: any) {
        this.adopt(Object.assign({}, this.prefs, { [key]: value }), true);
        this.pendingSaves++;
        this.http.post<{ preferences: Preferences }>(environment.api + 'user-preferences', { changes: { [key]: value } }).subscribe(
            res => this.settle(res.preferences),
            () => this.settle(null)
        );
    }

    // Removes this browser's cached copy (on logout), so the next person to sign in here never
    // sees the previous user's settings while theirs load.
    static clearCache() {
        ShellService.write(CACHE_KEY, null);
    }

    private fetchPreferences() {
        this.http.get<{ preferences: Preferences }>(environment.api + 'user-preferences').subscribe(
            res => {
                // A save still in flight would be undone by this older snapshot; its own response
                // brings the server copy instead.
                if (!this.pendingSaves && res && res.preferences) {
                    this.preferencesReady = true;
                    this.adopt(res.preferences, true);
                }
            },
            () => { }
        );
    }

    private settle(serverPrefs: Preferences) {
        this.pendingSaves = Math.max(0, this.pendingSaves - 1);
        if (!this.pendingSaves && serverPrefs) {
            this.adopt(serverPrefs, true);
        }
    }

    private adopt(prefs: Preferences, cache: boolean) {
        this.prefs = prefs || {};
        Object.keys(BODY_CLASSES).forEach(key => document.body.classList.toggle(BODY_CLASSES[key], !!this.prefs[key]));
        this.applyCollapsed();
        if (cache) {
            ShellService.write(CACHE_KEY, JSON.stringify({ owner: this.cacheOwner(), preferences: this.prefs }));
        }
        this.prefsSubject.next(this.prefs);
    }

    private readCache(): Preferences {
        try {
            const cached = JSON.parse(ShellService.read(CACHE_KEY));
            return cached && cached.owner === this.cacheOwner() && cached.preferences ? cached.preferences : {};
        } catch (e) {
            return {};
        }
    }

    // localStorage's `user` has no id, so the cache is tied to name + level; a different user
    // signing in on this browser simply starts from the server copy.
    private cacheOwner(): string {
        const u = this.user;
        return [u.user_first_name, u.user_last_name, u.user_level].join('|');
    }

    // ---- Sidebar state ----

    get collapsed(): boolean {
        if (this.narrow) {
            return this.narrowOverride === null ? true : this.narrowOverride;
        }
        return !!this.get('sidebar_collapsed', false);
    }

    // The collapse button: saved in a normal window; in a narrow one only until it widens.
    setCollapsed(collapsed: boolean) {
        if (this.narrow) {
            this.narrowOverride = collapsed;
            this.applyCollapsed();
            return;
        }
        this.set('sidebar_collapsed', collapsed);
    }

    setMobileMenuOpen(open: boolean) {
        document.body.classList.toggle('im-mobile-menu-open', open);
    }

    /** Fetches the profile picture once per session (the top bar asks on load). */
    loadAccountPhoto() {
        if (this.photoRequested) {
            return;
        }
        this.photoRequested = true;
        this.http.get<{ photo_url: string }>(environment.api + 'account').subscribe(
            res => this.photoSubject.next(res && res.photo_url || null),
            () => this.photoRequested = false
        );
    }

    /** After My Account saves a new picture (or removes it). */
    setAccountPhoto(url: string) {
        this.photoSubject.next(url || null);
    }

    isModeOn(mode: DisplayMode): boolean {
        return !!this.get(ShellService.modeKey(mode), false);
    }

    setMode(mode: DisplayMode, on: boolean) {
        this.set(ShellService.modeKey(mode), on);
    }

    private static modeKey(mode: DisplayMode): string {
        return mode.replace('-', '_');   // dark-mode -> dark_mode
    }

    // localStorage can throw (private mode, blocked storage); the shell must still render.
    static read(key: string): string {
        try {
            return localStorage.getItem(key);
        } catch (e) {
            return null;
        }
    }

    static write(key: string, value: string) {
        try {
            if (value === null) {
                localStorage.removeItem(key);
            } else {
                localStorage.setItem(key, value);
            }
        } catch (e) { }
    }
}
